import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Home Appliances", "InHome Furniture", "Hypermart", "Kitchen Lounges", "Emilia", "Careers", "General Enquiry"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [interest, setInterest] = useState("General Enquiry");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, interest });
      toast.success("Thank you! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setInterest("General Enquiry");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-0 border-b border-[#F7F2E9]/25 rounded-none py-3 font-body text-[#F7F2E9] placeholder:text-[#F7F2E9]/40 focus:outline-none focus:border-konattu-red transition-colors duration-300";

  return (
    <section id="contact" className="bg-konattu-charcoal py-24 md:py-36" data-testid="contact-section">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal><span className="font-mono text-xs tracking-[0.35em] text-konattu-yellow">/ SAY HELLO</span></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-bold text-[#F7F2E9] tracking-tight leading-[0.92] text-4xl md:text-6xl">
              Let&apos;s talk about<br />what you need.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 space-y-6 font-body">
              <div>
                <div className="font-mono text-[11px] tracking-[0.3em] text-konattu-red mb-2">HEAD OFFICE</div>
                <p className="text-[#F7F2E9]/80 text-lg leading-relaxed">Konattu Group<br />Thengana, Changanassery<br />Kottayam, Kerala, India</p>
              </div>
              <div>
                <div className="font-mono text-[11px] tracking-[0.3em] text-konattu-red mb-2">CALL US</div>
                <p className="text-[#F7F2E9]/80 text-lg leading-relaxed">
                  <a href="tel:8086568787" data-testid="contact-phone-main" className="hover:text-konattu-red transition-colors">8086568787</a> · General & Stores<br />
                  <a href="tel:8111803387" data-testid="contact-phone-emilia" className="hover:text-konattu-red transition-colors">8111803387</a> · Emilia, The Beauty Hub
                </p>
              </div>
              <div>
                <div className="font-mono text-[11px] tracking-[0.3em] text-konattu-red mb-2">WRITE TO US</div>
                <p className="text-[#F7F2E9]/80 text-lg leading-relaxed">
                  <a href="mailto:info@konattugroup.com" data-testid="contact-email-info" className="hover:text-konattu-red transition-colors">info@konattugroup.com</a> · General queries<br />
                  <a href="mailto:hr@konattugroup.com" data-testid="contact-email-hr" className="hover:text-konattu-red transition-colors">hr@konattugroup.com</a> · Careers
                </p>
              </div>
              <div>
                <div className="font-mono text-[11px] tracking-[0.3em] text-konattu-red mb-2">CAREERS</div>
                <p className="text-[#F7F2E9]/80 text-lg">Growing team of 250+. We&apos;re always hiring. Mention &quot;Careers&quot; and tell us your story.</p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
            <input data-testid="contact-name" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
            <div className="grid sm:grid-cols-2 gap-8">
              <input data-testid="contact-email" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={set("email")} />
              <input data-testid="contact-phone" className={inputCls} placeholder="Phone number" value={form.phone} onChange={set("phone")} />
            </div>

            <div>
              <div className="font-mono text-[11px] tracking-[0.3em] text-[#F7F2E9]/50 mb-4">I&apos;M INTERESTED IN</div>
              <div className="flex flex-wrap gap-2.5">
                {INTERESTS.map((it) => (
                  <button
                    key={it} type="button" onClick={() => setInterest(it)}
                    data-testid={`interest-${it.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-2 text-sm font-body border transition-colors duration-300 ${
                      interest === it ? "bg-konattu-red border-konattu-red text-white" : "border-[#F7F2E9]/25 text-[#F7F2E9]/70 hover:border-konattu-red"
                    }`}
                  >
                    {it}
                  </button>
                ))}
              </div>
            </div>

            <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} placeholder="Tell us more..." value={form.message} onChange={set("message")} />

            <button
              type="submit" disabled={loading} data-testid="contact-submit"
              className="group inline-flex items-center gap-3 bg-konattu-red text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-konattu-orange transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send message"}
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
